import * as React from 'react'
import { IUser } from 'static/interfaces'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import * as constants from '../../../static/constants'
import { Button } from '../../atoms'
import { FlashMessage } from '..'
import CropperUploader from '../CropperUploader'

interface IWindow {
  flashMessages: FlashMessage
}
declare var window: IWindow

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

interface IProps {
  user: IUser
  previewImage: any
  setPreviewImage: React.Dispatch<any>
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}

export const ProfileEditAvatarModal: React.FC<IProps> = ({
  user,
  previewImage,
  setPreviewImage,
  setShowModal,
}) => {
  const [cropping, setCropping] = React.useState(false)

  const setCroppedImage = croppedCanvas => {
    if (!croppedCanvas) {
      return
    }
    croppedCanvas.toBlob(blob => {
      setCropping(false)
      setPreviewImage({
        file: blob,
        url: croppedCanvas.toDataURL(),
      })
    }, 'image/*')
  }

  return (
    <Modal>
      <div className="Modal_Container">
        <p className="Modal_Title">{I18n.t('settings.avatar_image')}</p>
        <div className="Modal_Cropper">
          {cropping ? (
            <CropperUploader
              imageFile={previewImage.url}
              setCroppedImage={setCroppedImage}
              cancelCrop={() => setCropping(false)}
            />
          ) : (
            <div className="Modal_CurrentAvator">
              <img src={previewImage ? previewImage.url : user.avatar_url} />
              <div className="Modal_ImageSelect">
                <Button
                  small={true}
                  borderColor={constants.COLORS.divider}
                  textColor={constants.COLORS.subText}
                >
                  <label htmlFor="avatorFile">
                    {I18n.t('generic.select_image')}
                    <input
                      id="avatorFile"
                      type="file"
                      accept="image/png, image/jpeg"
                      onChange={event => {
                        setPreviewImage({
                          file: event.currentTarget.files[0],
                          url: URL.createObjectURL(event.currentTarget.files[0]),
                        })
                      }}
                    />
                  </label>
                </Button>
                {previewImage && (
                  <Button small={true} handleClick={() => setCropping(true)}>
                    {I18n.t('generic.trimming')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="Modal_Buttons">
          <Button
            handleClick={() => {
              setPreviewImage(null)
              setShowModal(false)
            }}
          >
            {I18n.t('generic.cancel')}
          </Button>
          <Button
            primary={true}
            handleClick={() => {
              setShowModal(false)
            }}
          >
            {I18n.t('generic.ok')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(34, 43, 55, 0.6);
  z-index: 6000;

  .Modal_Container {
    width: 680px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
    border-radius: 6px;
    background-color: ${constants.COLORS.white};
    color: ${constants.COLORS.black};
  }

  .Modal_Title {
    padding: 16px;
    font-weight: bold;
    border-bottom: 1px solid ${constants.COLORS.divider};
  }

  .Modal_Cropper {
    display: flex;
    justify-content: center;
    height: 250px;
    padding: 16px;
  }

  .Modal_CurrentAvator {
    width: 100%;
    height: 100%;
    text-align: center;

    > img {
      height: calc(100% - 36px);
    }

    .Modal_Trimming {
      padding: 0 4px;
      text-decoration: underline;
      border: none;
      color: ${constants.COLORS.primary};
      font-size: 14px;
    }

    > label {
      cursor: pointer;
      color: ${constants.COLORS.primary};
      border: solid 1px ${constants.COLORS.primary};
      border-radius: 3px;
      padding: 4px 8px;
      font-size: 14px;
    }

    input[type='file'] {
      display: none;
    }
  }

  .Modal_ImageSelect {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 12px;
  }

  .Modal_Buttons {
    display: flex;
    justify-content: flex-end;
    padding: 16px;
  }
`
